import { pipe } from '@helo/utils'
import { scaleLinear } from '@visx/scale'
import { AreaClosed } from '@visx/shape'
import type { ReactNode } from 'react'

import { useIsDark } from '../../../hooks/useIsDark'
import { chartMarkerFill, hexWithAlpha } from '../../../theme/colors'
import { Colors } from '../../Colors'
import { colorPaletteOptions } from '../../Colors'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import type {
  ChartDataPoint,
  ChartSeries,
  ResolvedChartSeries,
} from '../ChartData'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import { useChartInteraction } from '../ChartInteraction'
import { chartMarkOptions } from '../ChartMark'
import type { ChartSeriesOutput } from '../ChartSeries'
import { ChartSeriesUtils } from '../ChartSeries'
import type { ChartLineInput, ResolvedChartLineSeries } from './ChartLine'
import { ChartLineConstants } from './ChartLine.constants'
import {
  type ChartLineCurveType,
  type ChartLineStyle,
  chartLineOptions,
} from './ChartLine.options'

const clampOpacity = (value: number | undefined): number => {
  if (value == null || Number.isNaN(value)) {
    return ChartLineConstants.defaultFillOpacity
  }
  return Math.min(1, Math.max(0, value))
}

const deriveFillColor = (color: string, fillOpacity: number): string => {
  try {
    return hexWithAlpha(color, fillOpacity)
  } catch {
    return color
  }
}

const derivePointFillColor = (color: string): string => {
  try {
    return chartMarkerFill(color)
  } catch {
    return color
  }
}

const strokeDasharrayForStyle = (style: ChartLineStyle): string | undefined => {
  if (style === 'dashed') return ChartLineConstants.dasharray.dashed
  if (style === 'dotted') return ChartLineConstants.dasharray.dotted
  return undefined
}

const glyphSizeForRadius = (radius: number): number => Math.PI * radius ** 2

/**
 * SVG strokes are centered on the path. Figma ellipses use the same r for fill
 * and stroke; outer edge is r + strokeWidth/2.
 */
const pointStrokeRadius = (
  fillRadius: number,
  _strokeWidth: number = ChartLineConstants.pointStrokeWidth,
): number => fillRadius

const pointOuterRadius = (
  fillRadius: number,
  strokeWidth: number = ChartLineConstants.pointStrokeWidth,
): number => fillRadius + strokeWidth / 2

/** Stable SVG mask id so multi-series charts don't collide. */
const pointGapMaskId = (seriesKey: string): string =>
  `chart-line-point-gap-${seriesKey}`

const visiblePoints = (points: ChartDataPoint[]): ChartDataPoint[] =>
  points.filter(ChartLineConstants.isPointDefined)

type ResolveLineSeriesInput = {
  series: ResolvedChartSeries
  color: string
  fillColor: string
  highlightColor: string
  fillOpacity: number
  fillArea: boolean
  lineWidth: number
  lineStyle: ChartLineStyle
  curveType: ChartLineCurveType
  showDataPoints: boolean
  pointRadius: number
}

/** Maps style options → precalculated VisX-ready props on one series. */
const resolveLineSeries = ({
  series,
  color,
  fillColor,
  highlightColor,
  fillOpacity,
  fillArea,
  lineWidth,
  lineStyle,
  curveType,
  showDataPoints,
  pointRadius,
}: ResolveLineSeriesInput): ResolvedChartLineSeries => {
  const hasVisiblePoints = visiblePoints(series.points).length > 0

  return {
    ...series,
    visualization: 'line',
    color,
    fillColor,
    highlightColor,
    fillArea,
    fillOpacity,
    lineWidth,
    lineStyle,
    strokeDasharray: strokeDasharrayForStyle(lineStyle),
    curveType,
    showDataPoints,
    pointRadius,
    pointStrokeWidth: ChartLineConstants.pointStrokeWidth,
    pointHighlightStrokeWidth: ChartLineConstants.pointHighlightStrokeWidth,
    pointGapMaskId: pointGapMaskId(series.seriesKey),
    usePointGapMask: showDataPoints && hasVisiblePoints,
  }
}

const getPointX = (
  point: ChartDataPoint,
  xScale: (value: string) => number | undefined,
) => xScale(point.x) ?? 0

const getPointY = (point: ChartDataPoint, yScale: (value: number) => number) =>
  yScale(point.y ?? 0)

const isPointHighlighted = (
  point: ChartDataPoint,
  highlightedX: string | null,
): boolean => highlightedX != null && point.x === highlightedX

const pointStrokeColor = (
  series: ResolvedChartLineSeries,
  point: ChartDataPoint,
  highlightedX: string | null,
): string =>
  isPointHighlighted(point, highlightedX) ? series.highlightColor : series.color

const pointStrokeWidthFor = (
  series: ResolvedChartLineSeries,
  point: ChartDataPoint,
  highlightedX: string | null,
): number =>
  isPointHighlighted(point, highlightedX)
    ? series.pointHighlightStrokeWidth
    : series.pointStrokeWidth

export interface ChartLineAreaProps {
  series: ResolvedChartLineSeries
  xScale: (value: string) => number | undefined
  yScale: ReturnType<typeof scaleLinear<number>>
  y0: number
}

const Area = ({
  series,
  xScale,
  yScale,
  y0,
}: ChartLineAreaProps): ReactNode => {
  if (!series.fillArea) return null

  return (
    <AreaClosed<ChartDataPoint>
      data={series.points}
      x={point => getPointX(point, xScale)}
      y={point => getPointY(point, value => yScale(value) ?? 0)}
      y0={y0}
      yScale={yScale}
      curve={ChartLineConstants.curves[series.curveType]}
      defined={ChartLineConstants.isPointDefined}
      fill={deriveFillColor(series.color, series.fillOpacity)}
      fillOpacity={1}
    />
  )
}

export interface ChartLinePointsProps {
  series: ResolvedChartLineSeries
  xScale: (value: string) => number | undefined
  yScale: (value: number) => number
}

const Points = ({
  series,
  xScale,
  yScale,
}: ChartLinePointsProps): ReactNode => {
  const { highlightedX } = useChartInteraction()
  const isDark = useIsDark()
  if (!series.showDataPoints) return null

  return (
    <g>
      {visiblePoints(series.points).map((point, index) => {
        const cx = getPointX(point, xScale)
        const cy = getPointY(point, yScale)
        const strokeWidth = pointStrokeWidthFor(series, point, highlightedX)
        const pointFill = isDark ? series.fillColor : series.color
        return (
          <circle
            key={`${series.seriesKey}-point-${index}`}
            cx={cx}
            cy={cy}
            r={series.pointRadius}
            fill={pointFill}
            stroke={pointStrokeColor(series, point, highlightedX)}
            strokeWidth={strokeWidth}
          />
        )
      })}
    </g>
  )
}

export interface ChartLinePointGapMaskProps {
  series: ResolvedChartLineSeries
  /** Mount-unique id — dual Trigger/Expanded trees share seriesKey otherwise. */
  maskId: string
  xScale: (value: string) => number | undefined
  yScale: (value: number) => number
  innerWidth: number
  innerHeight: number
}

/** Punch holes in line/area so they stop at the outer edge of each data point. */
const PointGapMask = ({
  series,
  maskId,
  xScale,
  yScale,
  innerWidth,
  innerHeight,
}: ChartLinePointGapMaskProps): ReactNode => {
  if (!series.showDataPoints) return null

  const holes = visiblePoints(series.points)
  if (holes.length === 0) return null

  const holeRadius = pointOuterRadius(series.pointRadius)

  return (
    <defs>
      <mask id={maskId}>
        <rect width={innerWidth} height={innerHeight} fill="white" />
        {holes.map((point, index) => (
          <circle
            key={`${series.seriesKey}-gap-${index}`}
            cx={getPointX(point, xScale)}
            cy={getPointY(point, yScale)}
            r={holeRadius}
            fill="black"
          />
        ))}
      </mask>
    </defs>
  )
}

const isResolvedLineSeries = (
  series: ResolvedChartSeries,
): series is ResolvedChartLineSeries =>
  series.visualization === 'line' &&
  'curveType' in series &&
  'strokeDasharray' in series &&
  'pointGapMaskId' in series &&
  'pointStrokeWidth' in series

const lineFeatureOptions = {
  ...colorPaletteOptions,
  ...chartMarkOptions,
  ...chartLineOptions,
}

/** Resolve line, mark, and palette options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(lineFeatureOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(lineFeatureOptions),
      Options.validate(lineFeatureOptions),
    ),
  )

/** Map line style options onto resolved series. */
const setLineSeries = () =>
  RenderPipeline.updateOutput(
    (output: ChartSeriesOutput, ctx: { input: ChartLineInput }) => {
      const lineSeries = output.series.filter(
        ChartSeriesUtils.isVisualization('line'),
      )
      if (lineSeries.length === 0) {
        return {}
      }

      const fillOpacity = clampOpacity(
        ctx.input.fillOpacity ?? ChartLineConstants.defaultFillOpacity,
      )
      const pattern = Colors.getPattern(
        Colors.mapColorPalette(ctx.input.colorPalette),
      )
      const inputSeries = (ctx.input._series ?? []) as ChartSeries[]
      const colorByKey = ChartSeriesUtils.assignColors(
        output.series.map(seriesItem => seriesItem.seriesKey),
        Colors.mapColorPalette(ctx.input.colorPalette),
        ChartSeriesUtils.colorOverridesFromInput(inputSeries, output.series),
      )

      const series = output.series.map((seriesItem, index) => {
        if (seriesItem.visualization !== 'line') return seriesItem
        const inputItem =
          inputSeries.length === output.series.length
            ? inputSeries[index]
            : inputSeries[0]
        const color =
          inputItem?.color ??
          colorByKey[seriesItem.seriesKey] ??
          pattern.colors[0]!
        return resolveLineSeries({
          series: seriesItem,
          color,
          fillColor: inputItem?.fillColor ?? derivePointFillColor(color),
          highlightColor:
            inputItem?.highlightColor ?? Colors.getHighlight(pattern),
          fillOpacity,
          fillArea: ctx.input.fillArea ?? false,
          lineWidth:
            inputItem?.lineWidth ??
            ctx.input.lineWidth ??
            ChartLineConstants.defaultLineWidth,
          lineStyle:
            inputItem?.lineStyle ??
            ctx.input.lineStyle ??
            ChartLineConstants.defaultLineStyle,
          curveType:
            inputItem?.curveType ??
            ctx.input.curveType ??
            ChartLineConstants.defaultCurveType,
          showDataPoints: ctx.input.showDataPoints ?? true,
          pointRadius:
            ctx.input.pointRadius ?? ChartLineConstants.defaultPointRadius,
        })
      })

      return { series }
    },
  )

export const ChartLineInputParts = {
  setupInput,
}

export const ChartLineOutputParts = {
  setLineSeries,
}

export const ChartLineUtils = {
  clampOpacity,
  deriveFillColor,
  derivePointFillColor,
  strokeDasharrayForStyle,
  glyphSizeForRadius,
  pointStrokeRadius,
  pointOuterRadius,
  resolveLineSeries,
  getPointX,
  getPointY,
  visiblePoints,
  isPointHighlighted,
  pointStrokeColor,
  pointStrokeWidthFor,
  pointGapMaskId,
  isResolvedLineSeries,
  Area,
  Points,
  PointGapMask,
}
